import { useState, useEffect } from 'react'
import { AccountCredentialForm } from '@/components/integrations/AccountCredentialForm'
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { integrationsClient } from '@/lib/api/integrations/integrations.client'
import { toast } from 'sonner'
import { INTEGRATIONS } from '@super/shared-types'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useAuth } from '@/hooks/use-auth'
import * as z from 'zod'

const buildingLinkAdditionalSchema = z.object({
  defaultUnit: z.string().min(1, 'Default unit is required'),
})

export interface BuildingLinkConnectFormProps {
  isEditMode?: boolean
  onConnected: () => void
}

export function BuildingLinkConnectForm({
  isEditMode = false,
  onConnected,
}: BuildingLinkConnectFormProps) {
  const { currentCompany } = useAppAuth()
  const { isSuperAdmin } = useAuth()
  const [existingCredential, setExistingCredential] = useState<{
    accountIdentifier: string
  } | null>(null)
  const [defaultUnit, setDefaultUnit] = useState<string>('')
  const [isLoading, setIsLoading] = useState(isEditMode)

  useEffect(() => {
    if (!isEditMode || !currentCompany?.id) {
      setIsLoading(false)
      return
    }

    const fetchStatus = async () => {
      try {
        setIsLoading(true)
        const integrations = isSuperAdmin
          ? await integrationsClient.getAllAdminIntegrationsStatus(
              currentCompany.id,
            )
          : await integrationsClient.getAllIntegrationsStatus()
        const buildingLink = integrations.find(
          i =>
            i.type === INTEGRATIONS.BUILDING_LINK.TYPE ||
            i.name === INTEGRATIONS.BUILDING_LINK.NAME,
        )
        if (buildingLink?.connected && buildingLink.accountId) {
          setExistingCredential({ accountIdentifier: buildingLink.accountId })
          if (buildingLink.metadata?.defaultUnit) {
            setDefaultUnit(buildingLink.metadata.defaultUnit as string)
          }
        }
      } catch (error) {
        console.error('Error fetching integration status:', error)
        toast.error('Failed to load existing credentials')
      } finally {
        setIsLoading(false)
      }
    }
    fetchStatus()
  }, [isEditMode, currentCompany?.id, isSuperAdmin])

  const handleSubmit = async (data: {
    accountIdentifier: string
    password: string
    defaultUnit?: string
  }) => {
    if (!currentCompany?.id) {
      toast.error('No company selected')
      return
    }

    if (!data.defaultUnit) {
      toast.error('Default unit is required')
      return
    }

    const metadata = { defaultUnit: data.defaultUnit }

    try {
      if (isEditMode) {
        if (isSuperAdmin) {
          await integrationsClient.buildingLink.updateAdminCredentials(
            currentCompany.id,
            data.accountIdentifier,
            data.password,
            metadata,
          )
        } else {
          await integrationsClient.buildingLink.updateCredentials(
            data.accountIdentifier,
            data.password,
            metadata,
          )
        }
        toast.success('BuildingLink credentials updated successfully!')
      } else {
        if (isSuperAdmin) {
          await integrationsClient.buildingLink.createAdminCredentials(
            currentCompany.id,
            data.accountIdentifier,
            data.password,
            metadata,
          )
        } else {
          await integrationsClient.buildingLink.createCredentials(
            data.accountIdentifier,
            data.password,
            metadata,
          )
        }
        toast.success('BuildingLink integration connected successfully!')
      }
      onConnected()
    } catch (error) {
      console.error(
        `Error ${isEditMode ? 'updating' : 'connecting'} BuildingLink:`,
        error,
      )
      toast.error(
        `Failed to ${isEditMode ? 'update' : 'connect'} BuildingLink integration`,
      )
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
      </div>
    )
  }

  return (
    <AccountCredentialForm
      onSubmit={handleSubmit}
      accountIdentifierLabel="Username"
      accountIdentifierPlaceholder="your-username"
      passwordLabel="Password"
      passwordPlaceholder="Enter your password"
      submitButtonText={isEditMode ? 'Update' : 'Connect'}
      initialAccountIdentifier={existingCredential?.accountIdentifier}
      additionalSchema={buildingLinkAdditionalSchema}
      initialValues={{ defaultUnit }}
      confirmationText={
        isEditMode
          ? 'Update your credentials for BuildingLink'
          : 'Connect your credentials for BuildingLink'
      }
      requireEmail={false}
    >
      {({ form, isSubmitting }) => (
        <FormField
          control={form.control}
          name="defaultUnit"
          render={({ field }) => (
            <FormItem className="max-w-md">
              <FormLabel>Default unit</FormLabel>
              <FormControl>
                <Input
                  placeholder="1A"
                  disabled={isSubmitting}
                  value={field.value as string}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                />
              </FormControl>
              <FormDescription>
                The default unit # or resident name to use if no match is found
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </AccountCredentialForm>
  )
}
